// @ts-nocheck - Disable TypeScript checking for this file due to issues with AWS Amplify types
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { formatPhoneNumberForAPI, isValidInternationalMobile } from '../utils/formatters';
import { 
  signIn, 
  signUp, 
  signOut, 
  confirmSignUp, 
  resendSignUpCode, 
  resetPassword, 
  confirmResetPassword, 
  fetchUserAttributes, 
  getCurrentUser 
} from 'aws-amplify/auth';
import { useApiClient } from '../hooks/useApiClient';
import { useFeatureFlag } from './FeatureFlagContext';
import { 
  SEND_FORGOT_PASSWORD_SMS,
  VERIFY_FORGOT_PASSWORD_CODE,
  SEND_SMS_VERIFICATION,
  VERIFY_SMS_CODE,
  SEND_EMAIL_VERIFICATION,
  VERIFY_EMAIL_TOKEN
} from '@shared/graphql/queries/verification';

// Define the EmailPreferences type
interface EmailPreferences {
  marketingConsent: boolean;
  weeklyTipsSubscribed: boolean;
  marketingConsentDate?: Date;
}

// Define the User type
interface User {
  id: string;
  phoneNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  subscriptionLevel: string;
  promoSource?: string;
  emailPreferences?: EmailPreferences;
}

// Define the AuthContext type
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (phoneNumber: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<boolean>;
  verifyPhone: (phoneNumber: string, code: string) => Promise<boolean>;
  verifyEmailOtp: (email: string, code: string, marketingConsent?: boolean, weeklyTipsSubscribed?: boolean) => Promise<boolean>;
  resendEmailOtp: (email: string) => Promise<boolean>;
  sendEmailVerification: (email: string, userId: string) => Promise<boolean>;
  resendVerificationCode: (username: string) => Promise<boolean>;
  forgotPassword: (username: string) => Promise<boolean>;
  resetPassword: (username: string, code: string, newPassword: string) => Promise<boolean>;
  refreshAuthState: () => Promise<void>;
}

// Define the RegisterData type
interface RegisterData {
  phoneNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  marketingConsent?: boolean;
  affiliateCode?: string;
  promoSource?: string;
}

// Create the AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define the AuthProvider props
interface AuthProviderProps {
  children: ReactNode;
}

// Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  
  // Use the API client hook
  const { client, isLoading: isClientLoading, error: clientError, getClient } = useApiClient();
  
  // Use the feature flag hook to check if verification should be skipped
  const skipVerification = useFeatureFlag('skipVerification');
  
  // Log client error if present
  useEffect(() => {
    if (clientError) {
      console.error('API client error in AuthContext:', clientError);
    }
  }, [clientError]);

  // Check if the user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get the current authenticated user
        const currentUser = await getCurrentUser();
        
        // Fetch user attributes
        const attributes = await fetchUserAttributes();
        
        // Create a user object with basic information
        const userObj: User = {
          id: attributes.sub || '',
          phoneNumber: attributes.phone_number || '',
          email: attributes.email || '',
          firstName: attributes.given_name || '',
          lastName: attributes.family_name || '',
          subscriptionLevel: 'basic', // Default value, should be fetched from API
          emailPreferences: {
            marketingConsent: false,
            weeklyTipsSubscribed: false
          }
        };
        
        // Try to fetch email preferences from the backend
        try {
          // Get the API client
          let apiClient = client;
          
          // If client is not initialized, try to initialize it
          if (!apiClient) {
            apiClient = await getClient();
          }
          
          // GraphQL query to get user profile with email preferences
          const getUserQuery = `
            query GetUser {
              me {
                id
                subscriptionLevel
                promoSource
                emailPreferences {
                  marketingConsent
                  weeklyTipsSubscribed
                  marketingConsentDate
                }
              }
            }
          `;
          
          // Execute the query
          const response: any = await apiClient.graphql({
            query: getUserQuery
          });
          
          // Update the user object with the fetched data
          if (response?.data?.me) {
            userObj.subscriptionLevel = response.data.me.subscriptionLevel || 'basic';
            userObj.promoSource = response.data.me.promoSource;
            userObj.emailPreferences = response.data.me.emailPreferences || {
              marketingConsent: false,
              weeklyTipsSubscribed: false
            };
          }
        } catch (apiError) {
          console.error('Error fetching user profile:', apiError);
          // Don't throw an error here, as the user is already logged in
        }
        
        // Set the user in state
        setUser(userObj);
        setIsAuthenticated(true);
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []); // Remove dependencies to prevent infinite loop

  // Login function
  const login = async (phoneNumber: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Check if the user is already authenticated
      try {
        // Try to get the current user
        const currentUser = await getCurrentUser();
        console.log('User is already authenticated:', currentUser);
        
        // User is already authenticated, just fetch attributes and update state
        const attributes = await fetchUserAttributes();
        
        // Create a user object with basic information
        const userObj: User = {
          id: attributes.sub || '',
          phoneNumber: attributes.phone_number || phoneNumber,
          email: attributes.email || '',
          firstName: attributes.given_name || '',
          lastName: attributes.family_name || '',
          subscriptionLevel: 'basic', // Default value, should be fetched from API
          emailPreferences: {
            marketingConsent: false,
            weeklyTipsSubscribed: false
          }
        };
        
        // Try to fetch email preferences from the backend
        try {
          // Get the API client
          let apiClient = client;
          
          // If client is not initialized, try to initialize it
          if (!apiClient) {
            apiClient = await getClient();
          }
          
          // GraphQL query to get user profile with email preferences
          const getUserQuery = `
            query GetUser {
              me {
                id
                subscriptionLevel
                promoSource
                emailPreferences {
                  marketingConsent
                  weeklyTipsSubscribed
                  marketingConsentDate
                }
              }
            }
          `;
          
          // Execute the query
          const response: any = await apiClient.graphql({
            query: getUserQuery
          });
          
          // Update the user object with the fetched data
          if (response?.data?.me) {
            userObj.subscriptionLevel = response.data.me.subscriptionLevel || 'basic';
            userObj.promoSource = response.data.me.promoSource;
            userObj.emailPreferences = response.data.me.emailPreferences || {
              marketingConsent: false,
              weeklyTipsSubscribed: false
            };
          }
        } catch (apiError) {
          console.error('Error fetching user profile:', apiError);
          // Don't throw an error here, as the user is already logged in
        }
        
        // Set the user in state
        setUser(userObj);
        setIsAuthenticated(true);
        
        console.log('User already authenticated, updated state');
        return; // Exit early since we've already updated the state
      } catch (notAuthenticatedError) {
        // User is not authenticated, proceed with sign in
        console.log('User is not authenticated, proceeding with sign in');
      }
      
      // Format phone number for API
      const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber);
      
      // Validate the formatted phone number
      if (!isValidInternationalMobile(formattedPhoneNumber)) {
        throw new Error('Please enter a valid mobile number');
      }
      
      // Sign in the user
      const signInResult = await signIn({ username: formattedPhoneNumber, password });
      
      let userObj: User;
      
      try {
        // Fetch user attributes
        const attributes = await fetchUserAttributes();
        
        // Create a user object with basic information
        userObj = {
          id: attributes.sub || '',
          phoneNumber: attributes.phone_number || phoneNumber,
          email: attributes.email || '',
          firstName: attributes.given_name || '',
          lastName: attributes.family_name || '',
          subscriptionLevel: 'basic', // Default value, should be fetched from API
          emailPreferences: {
            marketingConsent: false,
            weeklyTipsSubscribed: false
          }
        };
        
        // Set the user in state
        setUser(userObj);
        setIsAuthenticated(true);
        
        // Try to fetch email preferences from the backend
        try {
          // Get the API client
          let apiClient = client;
          
          // If client is not initialized, try to initialize it
          if (!apiClient) {
            apiClient = await getClient();
          }
          
          // GraphQL query to get user profile with email preferences
          const getUserQuery = `
            query GetUser {
              me {
                id
                subscriptionLevel
                promoSource
                emailPreferences {
                  marketingConsent
                  weeklyTipsSubscribed
                  marketingConsentDate
                }
              }
            }
          `;
          
          // Execute the query
          const response: any = await apiClient.graphql({
            query: getUserQuery
          });
          
          // Update the user object with the fetched data
          if (response?.data?.me) {
            userObj.subscriptionLevel = response.data.me.subscriptionLevel || 'basic';
            userObj.promoSource = response.data.me.promoSource;
            userObj.emailPreferences = response.data.me.emailPreferences || {
              marketingConsent: false,
              weeklyTipsSubscribed: false
            };
            
            // Update the user state with the enhanced data
            setUser({...userObj});
          }
        } catch (apiError) {
          console.error('Error fetching user profile:', apiError);
          // Don't throw an error here, as the user is already logged in
        }
      } catch (attributesError) {
        console.error('Error fetching user attributes:', attributesError);
        
        // Check if this is an unconfirmed user error
        if (attributesError.name === 'UserNotConfirmedException' || 
            (attributesError.message && attributesError.message.includes('not confirmed'))) {
          console.log('User is not confirmed, redirecting to verification page');
          
          // Store the phone number for verification
          localStorage.setItem('tempPhoneNumber', phoneNumber);
          
          // Try to extract the user ID from the error message if available
          const userIdMatch = attributesError.message.match(/User ID: ([a-f0-9-]+)/i);
          if (userIdMatch && userIdMatch[1]) {
            localStorage.setItem('tempUserId', userIdMatch[1]);
          }
          
          // Throw a specific error that the login page can handle
          throw new Error('UNCONFIRMED_USER');
        }
        
        // If the error is about authentication, check if it's because the user is not confirmed
        if (attributesError.name === 'UserUnAuthenticatedException' || 
            (attributesError.message && attributesError.message.includes('User needs to be authenticated'))) {
          console.log('User is not properly authenticated, checking if unconfirmed');
          
          // Try to check if the user exists in Cognito but is not confirmed
          try {
            // Get the GraphQL endpoint
            const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
            
            // Format phone number for API
            const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber);
            
            // Execute the query to get user by phone
            const response = await fetch(graphqlEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                query: `
                  query GetUserByPhone($phoneNumber: String!) {
                    getUserByPhone(phoneNumber: $phoneNumber) {
                      id
                    }
                  }
                `,
                variables: {
                  phoneNumber: formattedPhoneNumber
                }
              })
            });
            
            const result = await response.json();
            
            // If we found a user, they likely exist but are not confirmed
            if (!result.errors && result.data?.getUserByPhone?.id) {
              console.log('Found user in Cognito, likely unconfirmed');
              
              // Store the phone number and user ID for verification
              localStorage.setItem('tempPhoneNumber', phoneNumber);
              localStorage.setItem('tempUserId', result.data.getUserByPhone.id);
              
              // Throw a specific error that the login page can handle
              throw new Error('UNCONFIRMED_USER');
            } else {
              // User not found or other error
              console.log('User is not properly authenticated and not found in Cognito');
              setUser(null);
              setIsAuthenticated(false);
              throw new Error('Authentication failed. Please try again.');
            }
          } catch (userCheckError) {
            // If there was an error checking the user, just proceed with the original error
            if (userCheckError.message === 'UNCONFIRMED_USER') {
              throw userCheckError;
            }
            
            console.log('Error checking if user is unconfirmed:', userCheckError);
            setUser(null);
            setIsAuthenticated(false);
            throw new Error('Authentication failed. Please try again.');
          }
        }
        
        // For other errors, we'll still try to create a minimal user object
        // but we'll check if we can get a valid token first
        try {
          // Try to get the current session to verify we have a valid token
          const currentUser = await getCurrentUser();
          
          // If we get here, we have a valid token
          userObj = {
            id: signInResult.userId || currentUser.userId || '',
            phoneNumber: phoneNumber,
            email: '',
            firstName: '',
            lastName: '',
            subscriptionLevel: 'basic',
            emailPreferences: {
              marketingConsent: false,
              weeklyTipsSubscribed: false
            }
          };
          
          setUser(userObj);
          setIsAuthenticated(true);
          
          console.log('User authenticated with minimal info');
        } catch (sessionError) {
          // If we can't get a valid session, the user is not authenticated
          console.error('Error getting current session:', sessionError);
          setUser(null);
          setIsAuthenticated(false);
          throw new Error('Authentication failed. Please try again.');
        }
      }
    } catch (error: any) {
      // If the error is UserAlreadyAuthenticatedException, try to refresh the auth state
      if (error.name === 'UserAlreadyAuthenticatedException') {
        console.log('User is already authenticated, refreshing auth state');
        await refreshAuthState();
      } 
      // If the error is UserNotConfirmedException, store the phone number and throw a specific error
      else if (error.name === 'UserNotConfirmedException') {
        console.log('User is not confirmed, redirecting to verification page');
        // Store the phone number and user ID for verification
        localStorage.setItem('tempPhoneNumber', phoneNumber);
        
        // Try to extract the user ID from the error message if available
        const userIdMatch = error.message.match(/User ID: ([a-f0-9-]+)/i);
        if (userIdMatch && userIdMatch[1]) {
          localStorage.setItem('tempUserId', userIdMatch[1]);
        }
        
        // Throw a specific error that the login page can handle
        throw new Error('UNCONFIRMED_USER');
      } else {
        console.error('Login error:', error);
        throw new Error(error.message || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (userData: RegisterData) => {
    try {
      setIsLoading(true);
      
      // Format phone number for API
      const formattedPhoneNumber = formatPhoneNumberForAPI(userData.phoneNumber);
      
      // Call the GraphQL registration mutation instead of Cognito signUp
      // This allows us to handle affiliate tracking in the backend
      try {
        console.log('=== REGISTER FUNCTION: Using GraphQL registration ===');
        console.log('Registration data:', {
          phoneNumber: formattedPhoneNumber,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          affiliateCode: userData.affiliateCode,
          promoSource: userData.promoSource
        });
        
        // GraphQL mutation for user registration
        const REGISTER_USER = `
          mutation RegisterUser($input: RegisterInput!) {
            register(input: $input) {
              success
              message
              cognitoId
            }
          }
        `;
        
        // Get the GraphQL endpoint
        const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
        
        console.log('REGISTER FUNCTION: Calling GraphQL registration mutation');
        
        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: REGISTER_USER,
            variables: {
              input: {
                phoneNumber: formattedPhoneNumber,
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                password: userData.password,
                marketingConsent: userData.marketingConsent,
                affiliateCode: userData.affiliateCode,
                promoSource: userData.promoSource
              }
            }
          })
        });
        
        const result = await response.json();
        
        if (result.errors) {
          console.error('REGISTER FUNCTION: GraphQL registration failed:', result.errors);
          throw new Error(result.errors[0]?.message || 'Registration failed');
        }
        
        const registerResponse = result.data?.register;
        if (!registerResponse?.success) {
          throw new Error(registerResponse?.message || 'Registration failed');
        }
        
        console.log('REGISTER FUNCTION: GraphQL registration successful');
        
        // Store the cognitoId for verification if available
        const cognitoId = registerResponse.cognitoId;
        if (cognitoId) {
          localStorage.setItem('tempUserId', cognitoId);
        }
        
        // Store other data for verification flow
        localStorage.setItem('tempPassword', userData.password);
        localStorage.setItem('tempEmail', userData.email);
        localStorage.setItem('tempPhoneNumber', formattedPhoneNumber);
        // Flag which verification method the backend will use
        localStorage.setItem('tempVerificationMethod', formattedPhoneNumber.startsWith('+1') ? 'email' : 'sms');
        
        // Store marketing consent for verification flow
        if (userData.marketingConsent !== undefined) {
          localStorage.setItem('marketingConsent', userData.marketingConsent.toString());
          localStorage.setItem('weeklyTipsSubscribed', userData.marketingConsent.toString());
        }
        
        console.log('REGISTER FUNCTION: Registration completed, stored data for verification');
        console.log('REGISTER FUNCTION: Navigating to verification page...');
        
        // Don't navigate here - let the RegisterPage handle navigation
        // This prevents race conditions and duplicate requests
        console.log('REGISTER FUNCTION: Registration completed, letting RegisterPage handle navigation');
        
      } catch (graphqlError) {
        console.error('REGISTER FUNCTION: GraphQL registration error:', graphqlError);
        throw graphqlError;
      }
      
      console.log('REGISTER FUNCTION: Registration process completed successfully');
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error: any) {
      console.error('Logout error:', error);
      throw new Error(error.message || 'Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify email function
  const verifyEmail = async (email: string, code: string) => {
    try {
      setIsLoading(true);
      await confirmSignUp({ username: email, confirmationCode: code });
      return true;
    } catch (error: any) {
      console.error('Email verification error:', error);
      throw new Error(error.message || 'Email verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify phone function - using GraphQL
  const verifyPhone = async (phoneNumber: string, code: string) => {
    try {
      setIsLoading(true);
      
      // Format phone number for API
      const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber);
      
      // Get the user ID from localStorage
      const userIdFromStorage = localStorage.getItem('tempUserId');
      if (!userIdFromStorage) {
        throw new Error('User ID not found. Please try registering again.');
      }
      const userId = userIdFromStorage;
      
      // Get marketing consent from localStorage
      const marketingConsentStr = localStorage.getItem('marketingConsent');
      const weeklyTipsSubscribedStr = localStorage.getItem('weeklyTipsSubscribed');
      
      // Convert to boolean
      const marketingConsent = marketingConsentStr === 'true';
      const weeklyTipsSubscribed = weeklyTipsSubscribedStr === 'true';
      
      console.log(`Verifying phone with marketing consent: ${marketingConsent}, weekly tips: ${weeklyTipsSubscribed}`);
      
      // GraphQL mutation for verifying SMS code
      const VERIFY_SMS_CODE = `
        mutation VerifySmsCode(
          $phoneNumber: String!,
          $code: String!,
          $userId: String!,
          $marketingConsent: Boolean,
          $weeklyTipsSubscribed: Boolean
        ) {
          verifySmsCode(
            phoneNumber: $phoneNumber,
            code: $code,
            userId: $userId,
            marketingConsent: $marketingConsent,
            weeklyTipsSubscribed: $weeklyTipsSubscribed
          ) {
            success
            message
          }
        }
      `;
      
      // Get the GraphQL endpoint from the environment variable or use the same approach as aws-config.ts
      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
      
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: VERIFY_SMS_CODE,
          variables: {
            phoneNumber,
            code,
            userId,
            marketingConsent,
            weeklyTipsSubscribed
          }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL verification failed:', result.errors);
        throw new Error(result.errors[0]?.message || 'Verification failed');
      }
      
      // Extract the verification response
      const verificationResponse = result.data?.verifySmsCode;
      if (!verificationResponse?.success) {
        throw new Error('Verification failed');
      }
      
      console.log('Phone verification succeeded');
      console.log('Welcome email message:', verificationResponse.message);
      
      // Store the welcome email message for the UI to potentially display
      if (verificationResponse.message) {
        localStorage.setItem('verificationSuccessMessage', verificationResponse.message);
      }
      
      // Email verification is disabled for now
      // Instead, proceed directly to authentication
      try {
        // Get user details from localStorage
        const email = localStorage.getItem('tempEmail');
        const password = localStorage.getItem('tempPassword');
        
        if (email && userId && password) {
          console.log(`Email verification disabled, proceeding directly to authentication for ${email}`);
          // Skip email verification step
          
          // Check if the user is already authenticated
          try {
            console.log('Checking if user is already authenticated...');
            
            try {
              // Try to get the current user
              const currentUser = await getCurrentUser();
              console.log('User is already authenticated:', currentUser);
              
              // User is already authenticated, just fetch attributes and update state
              const attributes = await fetchUserAttributes();
              
              // Create a user object with basic information
              const userObj: User = {
                id: attributes.sub || '',
                phoneNumber: attributes.phone_number || phoneNumber,
                email: attributes.email || '',
                firstName: attributes.given_name || '',
                lastName: attributes.family_name || '',
                subscriptionLevel: 'basic', // Default value, should be fetched from API
                emailPreferences: {
                  marketingConsent: false,
                  weeklyTipsSubscribed: false
                }
              };
              
              // Set the user in state
              setUser(userObj);
              setIsAuthenticated(true);
              
              // Clear localStorage items
              localStorage.removeItem('marketingConsent');
              localStorage.removeItem('weeklyTipsSubscribed');
              localStorage.removeItem('tempPassword');
              localStorage.removeItem('tempEmail');
              localStorage.removeItem('tempUserId');
              
              console.log('User state updated with current user data');
              console.log('Authentication state updated: isAuthenticated =', true);
              console.log('User state updated:', userObj);
            } catch (notAuthenticatedError) {
              // User is not authenticated, proceed with sign in
              console.log('User is not authenticated, proceeding with sign in');
              
              // Sign in the user
              await signIn({ username: phoneNumber, password });
              
              // Fetch user attributes
              const attributes = await fetchUserAttributes();
              
              // Create a user object with basic information
              const userObj: User = {
                id: attributes.sub || '',
                phoneNumber: attributes.phone_number || phoneNumber,
                email: attributes.email || '',
                firstName: attributes.given_name || '',
                lastName: attributes.family_name || '',
                subscriptionLevel: 'basic', // Default value, should be fetched from API
                emailPreferences: {
                  marketingConsent: false,
                  weeklyTipsSubscribed: false
                }
              };
              
              // Set the user in state
              setUser(userObj);
              setIsAuthenticated(true);
              
              // Clear localStorage items after successful login
              localStorage.removeItem('marketingConsent');
              localStorage.removeItem('weeklyTipsSubscribed');
              localStorage.removeItem('tempPassword');
              localStorage.removeItem('tempEmail');
              localStorage.removeItem('tempUserId');
              
              console.log('User auto-logged in successfully');
              console.log('Authentication state updated: isAuthenticated =', true);
              console.log('User state updated:', userObj);
            }
          } catch (loginError) {
            console.error('Error auto-logging in user:', loginError);
            // Don't fail the verification if auto-login fails
          }
        }
      } catch (emailVerificationError) {
        console.error('Error sending email verification:', emailVerificationError);
        // Don't fail the phone verification if email verification fails
      }
      
      return true;
    } catch (error: any) {
      console.error('Phone verification error:', error);
      throw new Error(error.message || 'Phone verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification code function - using GraphQL
  const resendVerificationCode = async (username: string) => {
    try {
      setIsLoading(true);
      
      // Ensure username is a string
      if (typeof username !== 'string' || !username) {
        throw new Error('Invalid phone number format');
      }
      
      // Format phone number for API
      const formattedPhoneNumber = formatPhoneNumberForAPI(username);
      
      console.log('=== RESEND_VERIFICATION_CODE FUNCTION: Starting ===');
      console.log('Call stack:', new Error().stack);
      
      // First try to use Brevo if we have a user ID
      const userIdFromStorage = localStorage.getItem('tempUserId');
      
      if (userIdFromStorage) {
        try {
          console.log(`RESEND_VERIFICATION_CODE FUNCTION: Using stored userId ${userIdFromStorage}`);
          
          // GraphQL mutation for sending SMS verification
          const SEND_SMS_VERIFICATION = `
            mutation SendSmsVerification($phoneNumber: String!, $userId: String!) {
              sendSmsVerification(phoneNumber: $phoneNumber, userId: $userId)
            }
          `;
          
          // Get the GraphQL endpoint from the environment variable or use the same approach as aws-config.ts
          const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
          
          console.log(`RESEND_VERIFICATION_CODE FUNCTION: Sending SMS verification to ${username} for user ${userIdFromStorage}`);
          
          const response = await fetch(graphqlEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              query: SEND_SMS_VERIFICATION,
              variables: {
                phoneNumber: username,
                userId: userIdFromStorage
              }
            })
          });
          
          const result = await response.json();
          
          if (result.errors) {
            console.warn('GraphQL SMS verification failed:', result.errors);
            throw new Error(result.errors[0]?.message || 'Failed to send verification code');
          }
          
          console.log('Verification code sent successfully');
          return true;
        } catch (verificationError) {
          console.error('Error sending verification code:', verificationError);
          // Don't throw here, try the next method
          console.log('Trying alternative method to send verification code...');
        }
      }
      
      // If we don't have a user ID or the previous method failed, try to get user info from the API
      try {
        console.log(`RESEND_VERIFICATION_CODE FUNCTION: No stored userId, looking up user by phone number ${formattedPhoneNumber}`);
        
        // GraphQL query to get user ID by phone number
        const GET_USER_BY_PHONE = `
          query GetUserByPhone($phoneNumber: String!) {
            getUserByPhone(phoneNumber: $phoneNumber) {
              id
            }
          }
        `;
        
        // Get the GraphQL endpoint
        const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
        
        console.log(`RESEND_VERIFICATION_CODE FUNCTION: Executing getUserByPhone query for ${formattedPhoneNumber}`);
        
        // Execute the query
        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: GET_USER_BY_PHONE,
            variables: {
              phoneNumber: formattedPhoneNumber
            }
          })
        });
        
        const result = await response.json();
        
        if (result.errors) {
          console.warn('GraphQL get user by phone failed:', result.errors);
          throw new Error(result.errors[0]?.message || 'Failed to find user account');
        }
        
        // Get the user ID from the result
        const userId = result.data?.getUserByPhone?.id;
        
        if (!userId) {
          throw new Error('User account not found');
        }
        
        // Store the user ID in localStorage for future use
        localStorage.setItem('tempUserId', userId);
        
        // Now send the verification code
        console.log(`RESEND_VERIFICATION_CODE FUNCTION: Found user ID ${userId}, now sending SMS verification`);
        
        const SEND_SMS_VERIFICATION = `
          mutation SendSmsVerification($phoneNumber: String!, $userId: String!) {
            sendSmsVerification(phoneNumber: $phoneNumber, userId: $userId)
          }
        `;
        
        console.log(`RESEND_VERIFICATION_CODE FUNCTION: Sending SMS verification to ${username} for user ${userId}`);
        
        const verificationResponse = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: SEND_SMS_VERIFICATION,
            variables: {
              phoneNumber: username,
              userId: userId
            }
          })
        });
        
        const verificationResult = await verificationResponse.json();
        
        if (verificationResult.errors) {
          console.warn('GraphQL SMS verification failed:', verificationResult.errors);
          throw new Error(verificationResult.errors[0]?.message || 'Failed to send verification code');
        }
        
        console.log('Verification code sent successfully');
        return true;
      } catch (apiError: any) {
        console.error('Error getting user or sending verification code:', apiError);
        throw new Error(apiError.message || 'Failed to resend verification code. Please try again later.');
      }
    } catch (error: any) {
      console.error('Resend verification code error:', error);
      throw new Error(error.message || 'Failed to resend verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify email OTP (used for +1 US/Canada registrations)
  const verifyEmailOtp = async (email: string, code: string, marketingConsent?: boolean, weeklyTipsSubscribed?: boolean) => {
    try {
      setIsLoading(true);
      const userId = localStorage.getItem('tempUserId') || '';
      const password = localStorage.getItem('tempPassword') || '';
      const phoneNumber = localStorage.getItem('tempPhoneNumber') || '';

      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || `${window.location.origin}/graphql`;

      const VERIFY_EMAIL_OTP = `
        mutation VerifyEmailOtp($userId: String!, $email: String!, $code: String!, $marketingConsent: Boolean, $weeklyTipsSubscribed: Boolean) {
          verifyEmailOtp(userId: $userId, email: $email, code: $code, marketingConsent: $marketingConsent, weeklyTipsSubscribed: $weeklyTipsSubscribed) {
            success
            message
          }
        }
      `;

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: VERIFY_EMAIL_OTP,
          variables: { userId, email, code, marketingConsent, weeklyTipsSubscribed }
        })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Verification failed');
      }

      const verificationResponse = result.data?.verifyEmailOtp;
      if (!verificationResponse?.success) {
        throw new Error('Verification failed');
      }

      if (verificationResponse.message) {
        localStorage.setItem('verificationSuccessMessage', verificationResponse.message);
      }

      // Auto sign-in after successful email verification
      try {
        if (email && userId && password) {
          try {
            const currentUser = await getCurrentUser();
            const attributes = await fetchUserAttributes();
            setUser({
              id: attributes.sub || '',
              phoneNumber: attributes.phone_number || phoneNumber,
              email: attributes.email || '',
              firstName: attributes.given_name || '',
              lastName: attributes.family_name || '',
              subscriptionLevel: 'basic',
              emailPreferences: { marketingConsent: false, weeklyTipsSubscribed: false }
            });
            setIsAuthenticated(true);
          } catch {
            await signIn({ username: phoneNumber, password });
            const attributes = await fetchUserAttributes();
            setUser({
              id: attributes.sub || '',
              phoneNumber: attributes.phone_number || phoneNumber,
              email: attributes.email || '',
              firstName: attributes.given_name || '',
              lastName: attributes.family_name || '',
              subscriptionLevel: 'basic',
              emailPreferences: { marketingConsent: false, weeklyTipsSubscribed: false }
            });
            setIsAuthenticated(true);
          }
        }
      } catch (loginError) {
        console.error('Error auto-logging in after email OTP verification:', loginError);
      }

      localStorage.removeItem('marketingConsent');
      localStorage.removeItem('weeklyTipsSubscribed');
      localStorage.removeItem('tempPassword');
      localStorage.removeItem('tempEmail');
      localStorage.removeItem('tempUserId');
      localStorage.removeItem('tempVerificationMethod');

      return true;
    } catch (error: any) {
      console.error('Email OTP verification error:', error);
      throw new Error(error.message || 'Email verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend email OTP (used for +1 US/Canada registrations)
  const resendEmailOtp = async (email: string) => {
    try {
      setIsLoading(true);
      const userId = localStorage.getItem('tempUserId') || '';
      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || `${window.location.origin}/graphql`;

      const SEND_EMAIL_OTP = `
        mutation SendEmailOtp($email: String!, $userId: String!) {
          sendEmailOtp(email: $email, userId: $userId)
        }
      `;

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: SEND_EMAIL_OTP, variables: { email, userId } })
      });

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to resend code');
      }

      return true;
    } catch (error: any) {
      console.error('Resend email OTP error:', error);
      throw new Error(error.message || 'Failed to resend verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password function
  const forgotPassword = async (username: string) => {
    try {
      setIsLoading(true);
      
      // Format phone number for API
      const formattedPhoneNumber = formatPhoneNumberForAPI(username);
      
      // Get the GraphQL endpoint from the environment variable or use the same approach as aws-config.ts
      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
      
      // Use the new GraphQL mutation to send forgot password SMS
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: SEND_FORGOT_PASSWORD_SMS,
          variables: {
            phoneNumber: formattedPhoneNumber
          }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL forgot password failed:', result.errors);
        throw new Error(result.errors[0]?.message || 'Failed to initiate password reset');
      }
      
      console.log('Forgot password SMS sent successfully');
      return true;
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw new Error(error.message || 'Failed to initiate password reset');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password function
  const confirmPasswordReset = async (username: string, code: string, newPassword: string) => {
    try {
      setIsLoading(true);
      
      // Format phone number for API
      const formattedPhoneNumber = formatPhoneNumberForAPI(username);
      
      // Get the GraphQL endpoint from the environment variable or use the same approach as aws-config.ts
      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
      
      // Use the new GraphQL mutation to verify forgot password code and reset password
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: VERIFY_FORGOT_PASSWORD_CODE,
          variables: {
            phoneNumber: formattedPhoneNumber,
            code,
            newPassword
          }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL reset password failed:', result.errors);
        throw new Error(result.errors[0]?.message || 'Failed to reset password');
      }
      
      console.log('Password reset successful');
      return true;
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw new Error(error.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Send email verification function - using GraphQL
  const sendEmailVerification = async (email: string, userId: string) => {
    try {
      setIsLoading(true);
      
      console.log(`Sending email verification to ${email} for user ${userId}`);
      
      // GraphQL mutation for sending email verification
      const SEND_EMAIL_VERIFICATION = `
        mutation SendEmailVerification($email: String!, $userId: String!) {
          sendEmailVerification(email: $email, userId: $userId)
        }
      `;
      
      // Get the GraphQL endpoint from the environment variable or use the same approach as aws-config.ts
      const graphqlEndpoint = import.meta.env.VITE_API_ENDPOINT || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : 'http://localhost:5173/graphql');
      
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: SEND_EMAIL_VERIFICATION,
          variables: {
            email,
            userId
          }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL email verification failed:', result.errors);
        throw new Error(result.errors[0]?.message || 'Failed to send verification email');
      }
      
      console.log('Email verification sent successfully');
      return true;
    } catch (error: any) {
      console.error('Email verification error:', error);
      throw new Error(error.message || 'Failed to send verification email');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh the authentication state
  const refreshAuthState = async () => {
    try {
      setIsLoading(true);
      console.log('Refreshing authentication state...');
      
      try {
        // Try to get the current user
        const currentUser = await getCurrentUser();
        console.log('User is authenticated:', currentUser);
        
        try {
          // Fetch user attributes
          const attributes = await fetchUserAttributes();
          
          // Check if the user's phone number is verified
          const phoneVerified = attributes.phone_number_verified === 'true';
          // +1 users are verified via email OTP — skip the phone verified check for them
          const isEmailVerifiedUser = (attributes.phone_number || '').startsWith('+1');

          // If phone number is not verified, redirect to verification page
          if (!phoneVerified && !isEmailVerifiedUser) {
            console.log('User phone number is not verified, redirecting to verification page');
            
            // Store the phone number in localStorage for verification
            if (attributes.phone_number) {
              localStorage.setItem('tempPhoneNumber', attributes.phone_number);
            }
            
            // Store the user ID in localStorage for verification
            localStorage.setItem('tempUserId', attributes.sub || '');
            
            // Set user as not authenticated
            setUser(null);
            setIsAuthenticated(false);
            
            // Redirect to verification page
            if (typeof window !== 'undefined') {
              window.location.href = '/verify-phone';
            }
            
            return;
          }
          
          // Create a user object with basic information
          const userObj: User = {
            id: attributes.sub || '',
            phoneNumber: attributes.phone_number || '',
            email: attributes.email || '',
            firstName: attributes.given_name || '',
            lastName: attributes.family_name || '',
            subscriptionLevel: 'basic', // Default value, should be fetched from API
            emailPreferences: {
              marketingConsent: false,
              weeklyTipsSubscribed: false
            }
          };
          
          // Set the user in state
          setUser(userObj);
          setIsAuthenticated(true);
          
          console.log('Authentication state refreshed: isAuthenticated =', true);
          console.log('User state updated:', userObj);
        } catch (attributesError) {
          console.error('Error fetching user attributes:', attributesError);
          
          // If we can't fetch attributes but have a current user, still consider the user authenticated
          // but with minimal information
          const userObj: User = {
            id: currentUser.userId || '',
            phoneNumber: '',
            email: '',
            firstName: '',
            lastName: '',
            subscriptionLevel: 'basic',
            emailPreferences: {
              marketingConsent: false,
              weeklyTipsSubscribed: false
            }
          };
          
          setUser(userObj);
          setIsAuthenticated(true);
          
          console.log('Authentication state refreshed with minimal info: isAuthenticated =', true);
        }
      } catch (error: any) {
        console.log('User is not authenticated');
        
        // Check if this is an unconfirmed user error
        if (error.name === 'UserNotConfirmedException' || 
            (error.message && error.message.includes('not confirmed'))) {
          console.log('User is not confirmed, redirecting to verification page');
          
          // Try to extract the phone number from the error message
          const phoneMatch = error.message.match(/phone number: (\+\d+)/i);
          if (phoneMatch && phoneMatch[1]) {
            localStorage.setItem('tempPhoneNumber', phoneMatch[1]);
          }
          
          // Try to extract the user ID from the error message
          const userIdMatch = error.message.match(/User ID: ([a-f0-9-]+)/i);
          if (userIdMatch && userIdMatch[1]) {
            localStorage.setItem('tempUserId', userIdMatch[1]);
          }
          
          // Redirect to verification page
          if (typeof window !== 'undefined') {
            window.location.href = '/verify-phone';
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          console.log('Authentication state refreshed: isAuthenticated =', false);
        }
      }
    } catch (error) {
      console.error('Error refreshing authentication state:', error);
      // In case of any error, set user as not authenticated
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create the context value
  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login,
    register,
    logout,
    verifyEmail,
    verifyPhone,
    verifyEmailOtp,
    resendEmailOtp,
    sendEmailVerification,
    resendVerificationCode,
    forgotPassword,
    resetPassword: confirmPasswordReset,
    refreshAuthState,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Create a hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
